import { ComboBoxItem, splitArray } from "@/utils/helper.util";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { CompanySelectedOptionsDto } from "@advanced-sales-technologies/backend-client/models/CompanySelectedOptionsDto";
import { IndustryQueryEntryDto } from "@advanced-sales-technologies/backend-client/models/IndustryQueryEntryDto";
import type { ListConfigurationDto } from "@advanced-sales-technologies/backend-client/models/ListConfigurationDto";

import { LocationOption, Range, SearchParams } from "@/hooks/api/search/useInfiniteSearch";

type ProcessRangeValues = {
  ranges: Range[];
  freeRange: { min?: string; max?: string };
  multiplyFreeRangeBy?: number;
};

const processRangeValues = ({ ranges, freeRange, multiplyFreeRangeBy = 1 }: ProcessRangeValues) => {
  const newRange = [...ranges.map((range) => ({ min: range.min, max: range.max }))];

  if (freeRange) {
    let { min, max } = freeRange;

    if (min === "") {
      min = undefined;
    }
    if (max === "") {
      max = undefined;
    }

    // Convert strings to numbers if they are defined and not empty
    const minNum = min !== undefined ? Number(min) : undefined;
    const maxNum = max !== undefined ? Number(max) : undefined;

    // If conversion results in NaN, set to undefined
    const validMin = minNum !== undefined && !isNaN(minNum) ? minNum : undefined;
    const validMax = maxNum !== undefined && !isNaN(maxNum) ? maxNum : undefined;

    // Swap if min is greater than max
    let finalMin = validMin;
    let finalMax = validMax;
    if (finalMin !== undefined && finalMax !== undefined && finalMin > finalMax) {
      [finalMin, finalMax] = [finalMax, finalMin];
    }

    if (finalMin !== undefined || finalMax !== undefined) {
      newRange.push({
        min: finalMin && finalMin * multiplyFreeRangeBy,
        max: finalMax && finalMax * multiplyFreeRangeBy,
      });
    }
  }

  return newRange;
};

/**
 * Merge any number of string arrays and return a new array
 * with only unique entries, preserving first-seen order.
 */
function mergeUnique(...arrays: string[][]): string[] {
  // Flatten all the arrays into one
  const combined = arrays.flat();
  // Use a Set to drop duplicates while keeping insertion order
  return Array.from(new Set(combined));
}

export const buildSearchRequestBody = (searchParams: SearchParams): ListConfigurationDto => {
  const {
    companyInclude,
    companyExclude,
    showTerminated,
    locationInclude,
    locationExclude,
    hq,
    industryExclude,
    industryInclude,
    revenueGrowth,
    revenueGrowthFree,
    foundingDate,
    predefinedFoundingDate,
    keywords,
    keywordsExclude,
    advancedIndustryIncludeMainIds,
    advancedIndustryExcludeMainIds,
  } = searchParams;

  const headcount = processRangeValues({ ranges: searchParams.headcount, freeRange: searchParams.headcountFree });
  const revenue = processRangeValues({
    ranges: searchParams.revenue,
    freeRange: searchParams.revenueFree,
    multiplyFreeRangeBy: 1_000_000,
  });
  const revenueGrowthValues = processRangeValues({
    ranges: revenueGrowth,
    freeRange: revenueGrowthFree,
  });

  const { ids: includedCompanyIds, names: includedCompanyQueries } = splitArray(companyInclude);
  const { ids: excludedCompanyIds, names: excludedCompanyQueries } = splitArray(companyExclude);

  const filterOnlyIds = (item: ComboBoxItem[]) => item.map((item) => item.id);

  const prepareLocation = (item: LocationOption[]) =>
    item.map((item) => ({
      country_code: item.country_code?.toUpperCase(),
      value: item.name,
      target: item.target ?? "FREE_FORM",
    }));

  const includedLocationQueries = prepareLocation(locationInclude);
  const excludedLocationQueries = prepareLocation(locationExclude);

  /** INDUSTRIES */
  // SALESY Target
  const includedIndustryIds = filterOnlyIds(industryInclude);
  const includedIndustryIdsWithStandard = includedIndustryIds.map((id) => ({
    id: id,
    target: "SALESY" as IndustryQueryEntryDto.target,
  }));
  const excludedIndustryIds = filterOnlyIds(industryExclude);
  const excludedIndustryIdsWithStandard = excludedIndustryIds.map((id) => ({
    id: id,
    target: "SALESY" as IndustryQueryEntryDto.target,
  }));

  // NACE Target
  const advancedIndustryIncludeMainIdsWithStandard = advancedIndustryIncludeMainIds.map((id) => ({
    id: id,
    target: "NACE" as IndustryQueryEntryDto.target,
  }));
  const advancedIndustryExcludeMainIdsWithStandard = advancedIndustryExcludeMainIds.map((id) => ({
    id: id,
    target: "NACE" as IndustryQueryEntryDto.target,
  }));

  return {
    excluded_company_ids: excludedCompanyIds || [],
    excluded_queries: excludedCompanyQueries || [],
    excluded_locations: excludedLocationQueries || [],
    excluded_industries: [...excludedIndustryIdsWithStandard, ...advancedIndustryExcludeMainIdsWithStandard],
    included_company_ids: includedCompanyIds || [],
    included_queries: includedCompanyQueries || [],
    included_locations: includedLocationQueries || [],
    included_industries: [...includedIndustryIdsWithStandard, ...advancedIndustryIncludeMainIdsWithStandard],
    only_hqs: hq,
    show_non_active: showTerminated,
    headcount_ranges: headcount,
    revenue_ranges: revenue,
    revenue_growth_ranges: revenueGrowthValues,
    included_keywords: keywords,
    excluded_keywords: keywordsExclude,
    founding_date: {
      start: foundingDate.min || undefined,
      end: foundingDate.max || undefined,
    },
    predefined_founding_date_ranges: predefinedFoundingDate?.map(({ value }) => value) || [],
  };
};

/**
 * Helper function to build the company selection object, to keep selection when another selection is made
 * @param company
 */
export const buildCompanySelection = (company: CompanyEntryDto): CompanySelectedOptionsDto => {
  const { info, office, office_hq, point_of_contact, socials } = company;

  const cp = point_of_contact?.is_external
    ? {
        external_contact_person_id: point_of_contact?.id,
        external_contact_person_phone_id: point_of_contact?.contact_info?.phone?.id,
        external_contact_person_linkedin_id: point_of_contact?.socials?.linkedin?.id,
      }
    : {
        contact_person_id: point_of_contact?.id,
        contact_person_phone_id: point_of_contact?.contact_info?.phone?.id,
        contact_person_linkedin_id: point_of_contact?.socials?.linkedin?.id,
      };

  const emailId = point_of_contact?.contact_info?.email?.is_external
    ? { external_contact_person_email_id: point_of_contact?.contact_info?.email?.id }
    : {
        contact_person_email_id: point_of_contact?.contact_info?.email?.id,
      };

  return {
    office_id: office?.id,
    office_phone_id: office?.contact_info?.phone?.id,
    office_email_id: office?.contact_info?.email?.id,
    office_hq_phone_id: office_hq?.contact_info?.phone?.id,
    office_hq_email_id: office_hq?.contact_info?.email?.id,
    company_phone_id: info?.contact_info?.phone?.id,
    company_email_id: info?.contact_info?.email?.id,
    company_website_id: info?.website?.id,
    company_linkedin_id: socials?.linkedin.id,
    ...cp,
    ...emailId,
  };
};

function isCompanyEntryDto(value: unknown): value is CompanyEntryDto {
  return typeof value === "object" && value !== null && "id" in value;
}

/**
 * Builds a combined selection for a list of companies, which can be either CompanyEntryDto or string IDs.
 * @param items
 */
export const buildCompanySelectionCombined = (items: (CompanyEntryDto | string)[]) =>
  items.map((item) => {
    if (isCompanyEntryDto(item)) {
      return {
        company_id: item.id,
        selection: buildCompanySelection(item),
      };
    }

    return { company_id: item };
  });
